import Link from "next/link";
import { ArrowRight, Check, Layers, Rocket, Users } from "lucide-react";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";
import PageHero from "@/components/landing-page/PageHero";
import FinalCta from "@/components/landing-page/home/FinalCta";
import Reveal from "@/components/ui/Reveal";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

const solutions = [
  {
    id: "enterprise-ai-upskilling",
    Icon: Rocket,
    title: "Enterprise AI Upskilling",
    tagline: "Build AI capability across the whole organization.",
    body: "An organization-wide program that starts with an assessment of every employee and gives each person a path from where they are, rather than one course for everyone.",
    bestFor: "Organizations rolling out AI across every function",
    includes: ["Workforce-wide AI capability assessment", "Personalized paths for every employee", "Organization-level progress reporting"],
  },
  {
    id: "role-based-training",
    Icon: Users,
    title: "Role-Based Training",
    tagline: "Programs for every function and seniority.",
    body: "Dedicated tracks for the teams that need AI most, from sales and operations to engineering and leadership, each built around how that role actually uses AI.",
    bestFor: "L&D teams targeting specific functions or levels",
    includes: ["Tracks by function and seniority", "Practice on role-specific scenarios", "Expert validation of role skills"],
  },
  {
    id: "custom-programs",
    Icon: Layers,
    title: "Custom Programs",
    tagline: "Co-built around your context and goals.",
    body: "We co-build content around your industry, tools, policies and priorities, then inaraX personalizes it for every employee by role and seniority.",
    bestFor: "Organizations with specific tools, policies or regulatory needs",
    includes: ["Content co-built with your team", "Your context in every exercise", "Dedicated program support"],
  },
] as const;

export default function SolutionsPage() {
  return (
    <LandingPageShell>
      <PageHero
        eyebrow="Solutions"
        title="AI learning programs for every part of your organization."
        description="Whether you are upskilling the whole workforce, training specific roles, or building something around your own context, inaraX personalizes it for every person."
      />

      <section className="bg-mkt-surface px-margin-mobile md:px-margin-desktop py-size-xl">
        <div className="max-w-[1440px] mx-auto flex flex-col gap-gutter">
          {solutions.map(({ id, Icon, title, tagline, body, bestFor, includes }) => (
            <Reveal key={id}>
              <article
                id={id}
                aria-labelledby={`${id}-heading`}
                className="scroll-mt-28 grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-size-lg rounded-3xl border border-mkt-primary/20 bg-white p-size-md sm:p-size-lg shadow-[0_4px_16px_rgba(0,60,185,0.10)]"
              >
                <div>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-mkt-primary/10 text-mkt-primary mb-size-md">
                    <Icon aria-hidden="true" strokeWidth={1.5} className="h-6 w-6" />
                  </span>
                  <h2
                    id={`${id}-heading`}
                    className="font-display-lg text-[clamp(26px,2.6vw,40px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black"
                  >
                    {title}
                  </h2>
                  <p className="mt-size-xs font-headline-md text-headline-sm text-mkt-primary-container">{tagline}</p>
                  <p className="mt-size-md font-body-lg text-body-lg text-mkt-on-surface-variant">{body}</p>
                  <Link
                    href={BOOK_DEMO_HREF}
                    className="group mt-size-md inline-flex items-center gap-1.5 font-label-md text-label-md font-bold text-mkt-primary border-b-2 border-mkt-primary"
                  >
                    Talk to us about {title.toLowerCase()}
                    <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
                <div className="rounded-xl bg-mkt-primary-container/[0.06] border border-mkt-primary/15 p-size-md flex flex-col gap-size-md">
                  <div>
                    <h3 className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary mb-size-xs">Best for</h3>
                    <p className="font-body-md text-body-md text-mkt-on-surface">{bestFor}</p>
                  </div>
                  <div>
                    <h3 className="font-label-md text-label-md uppercase tracking-widest text-mkt-primary mb-size-sm">
                      What&rsquo;s included
                    </h3>
                    <ul className="list-none pl-0 ml-0 flex flex-col gap-size-sm">
                      {includes.map((item) => (
                        <li key={item} className="flex items-start gap-size-sm font-body-md text-body-md text-mkt-on-surface">
                          <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-mkt-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <FinalCta />
    </LandingPageShell>
  );
}
